import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { BaseballCursor } from "@/components/BaseballCursor";
import { Nav } from "@/components/Nav";
import { SiteFooter } from "@/components/SiteFooter";
import { FadeUp } from "@/components/FadeUp";

const DIVISION_ORDER = [
  "AL East",
  "AL Central",
  "AL West",
  "NL East",
  "NL Central",
  "NL West",
];

// ---------------------------------------------------------------------------
// Data hook
// ---------------------------------------------------------------------------

/**
 * Fetches the briefing payload for the given token. Returns {data, error,
 * loading}. The /data endpoint also records the view.
 */
function useBriefingData(token) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const dataResp = await api.get(`/briefing/${token}/data`);
        if (cancelled) return;
        setData(dataResp.data);
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        setError(
          status === 404
            ? "This briefing link is no longer valid."
            : "We couldn't load the briefing right now. Try again in a moment.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return { data, error, loading };
}

// ---------------------------------------------------------------------------
// Tiny presentational helpers
// ---------------------------------------------------------------------------

function SectionHeader({ eyebrow, title, subtitle }) {
  return (
    <div className="mb-6 pb-4 border-b-2 border-brand-black">
      {eyebrow && (
        <p className="flex items-center gap-2.5 font-mono text-[10px] font-bold tracking-[0.24em] text-brand-red uppercase">
          <span aria-hidden="true" className="inline-block w-1.5 h-1.5 bg-brand-red" />
          {eyebrow}
        </p>
      )}
      <h2 className="mt-2 font-serif text-xl md:text-2xl font-black text-brand-black tracking-tight">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-1 text-sm text-brand-muted-strong">{subtitle}</p>
      )}
    </div>
  );
}

function PrimaryColorBar({ color }) {
  return (
    <span
      aria-hidden="true"
      className="absolute top-0 left-0 right-0 h-1.5"
      style={{ background: color || "#1a1a2e" }}
    />
  );
}

function ResultBadge({ won }) {
  if (typeof won !== "boolean") return null;
  const winClass = "bg-brand-black text-white";
  const lossClass = "bg-brand-red-soft text-brand-red border border-brand-red/30";
  return (
    <span
      className={`inline-flex items-center justify-center w-7 h-7 font-mono font-bold text-xs ${
        won ? winClass : lossClass
      }`}
    >
      {won ? "W" : "L"}
    </span>
  );
}

function formatGameTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    });
  } catch {
    return "";
  }
}

function standingsRowClass(idx) {
  if (idx === 0) return "bg-brand-surface/60";
  if (idx % 2 === 0) return "bg-white";
  return "bg-brand-paper/60";
}

function stableNewsKey(item, idx) {
  if (typeof item === "string") return `${item.slice(0, 32)}-${idx}`;
  const text = item.text || item.description || item.note || "";
  return `${item.kind || "news"}-${text.slice(0, 32)}-${idx}`;
}

function stableTxKey(item, idx) {
  return `${item.team || ""}-${item.player || ""}-${item.transaction_date || ""}-${idx}`;
}

function stableHeadlineKey(line, idx) {
  return `${line.slice(0, 40)}-${idx}`;
}

function stableMoverKey(m, idx) {
  const teamPart = m.team?.abbr || m.team?.name || (typeof m.team === "string" ? m.team : "");
  return `${m.css_class || "mv"}-${teamPart}-${idx}`;
}

function stableTrendKey(item, idx) {
  const teamPart =
    item.team?.abbr || item.team?.name || (typeof item.team === "string" ? item.team : "");
  return `${teamPart}-${item.stat_value || ""}-${idx}`;
}

// ---------------------------------------------------------------------------
// Headlines
// ---------------------------------------------------------------------------

function HeadlinesCard({ headlines }) {
  if (!headlines || headlines.length === 0) return null;
  return (
    <section
      data-testid="briefing-headlines"
      className="rounded-2xl bg-white border border-brand-black/10 shadow-card p-6 md:p-8"
    >
      <SectionHeader
        eyebrow="League pulse"
        title="Today's Headlines"
        subtitle="The five storylines shaping the league right now."
      />
      <ol className="space-y-3">
        {headlines.map((line, idx) => (
          <li
            key={stableHeadlineKey(line, idx)}
            className="flex gap-4 items-start"
            data-testid={`briefing-headline-${idx}`}
          >
            <span className="shrink-0 w-7 h-7 inline-flex items-center justify-center rounded-md bg-brand-black text-white font-bold text-xs">
              {idx + 1}
            </span>
            <p className="text-[15px] md:text-base text-brand-black leading-relaxed pt-0.5">
              {line}
            </p>
          </li>
        ))}
      </ol>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Team deep dive — decomposed
// ---------------------------------------------------------------------------

function DeepDiveHeader({ team, standing }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-5">
      <div className="min-w-0">
        <p className="text-[11px] font-bold tracking-[0.18em] text-brand-muted-strong uppercase">
          Your Team
        </p>
        <h3 className="mt-1 font-serif text-2xl md:text-3xl font-black text-brand-black leading-tight">
          {team.name}
        </h3>
        {standing && (
          <p className="mt-1 text-sm font-semibold text-brand-muted-strong">
            {standing.w}-{standing.l}
            {standing.pct ? ` · ${standing.pct}` : ""}
            {standing.gb && standing.gb !== "-" ? ` · ${standing.gb} GB` : ""}
            {standing.division ? ` · ${standing.division}` : ""}
            {standing.division_rank ? ` (#${standing.division_rank})` : ""}
          </p>
        )}
      </div>
      <span
        className="shrink-0 inline-flex items-center justify-center min-w-[3.5rem] h-12 px-3 rounded-lg text-white font-extrabold text-sm tracking-wider"
        style={{ background: team.primary_color || "#1a1a2e" }}
      >
        {team.abbr}
      </span>
    </div>
  );
}

function NotableBattersList({ batters }) {
  if (!batters || batters.length === 0) return null;
  return (
    <div className="mt-2">
      <p className="text-[10px] font-bold tracking-[0.18em] text-brand-muted-strong uppercase mb-1">
        Top bats
      </p>
      <ul className="space-y-0.5">
        {batters.slice(0, 3).map((b) => (
          <li key={`${b.name}-${b.ab}-${b.h}`} className="text-sm text-brand-black">
            <span className="font-semibold">{b.name}</span>
            <span className="text-brand-muted-strong">
              {" "}
              — {b.h}-for-{b.ab}
              {b.hr ? `, ${b.hr} HR` : ""}
              {b.rbi ? `, ${b.rbi} RBI` : ""}
              {b.r ? `, ${b.r} R` : ""}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LastGameBlock({ lastGame, teamAbbr }) {
  if (!lastGame) return null;
  const homeVsAway = lastGame.home ? "vs" : "@";
  return (
    <div className="mb-5 rounded-xl bg-brand-paper border border-brand-black/5 p-4">
      <div className="flex items-center justify-between gap-3 mb-2">
        <p className="text-[10px] font-bold tracking-[0.18em] text-brand-muted-strong uppercase">
          Last Game
        </p>
        <ResultBadge won={lastGame.won} />
      </div>
      <p className="text-base font-bold text-brand-black">
        {teamAbbr} {lastGame.team_runs}, {lastGame.opponent_abbr || "OPP"}{" "}
        {lastGame.opp_runs}{" "}
        <span className="text-brand-muted-strong font-normal">
          {homeVsAway} {lastGame.opponent_name || lastGame.opponent_abbr}
        </span>
      </p>
      {lastGame.sp_line && lastGame.sp_line.name && (
        <p className="mt-2 text-sm text-brand-muted-strong">
          <span className="font-semibold text-brand-black">SP:</span>{" "}
          {lastGame.sp_line.name} · {lastGame.sp_line.ip} IP, {lastGame.sp_line.h} H,{" "}
          {lastGame.sp_line.er} ER, {lastGame.sp_line.bb} BB, {lastGame.sp_line.k} K
        </p>
      )}
      <NotableBattersList batters={lastGame.notable_batters} />
    </div>
  );
}

function EdgeAnalysisBlock({ text }) {
  if (!text) return null;
  return (
    <div className="mb-5">
      <p className="text-[10px] font-bold tracking-[0.18em] text-brand-red uppercase mb-2">
        Matchup
      </p>
      <p className="text-[15px] text-brand-black leading-relaxed">{text}</p>
    </div>
  );
}

function ProbablePitchersLine({ ourSp, oppSp }) {
  if (!ourSp && !oppSp) return null;
  return (
    <p className="mt-1.5 text-xs text-brand-muted-strong">
      <span className="font-semibold text-brand-black">SP:</span>{" "}
      {ourSp?.name || "TBD"} {ourSp?.era ? `(${ourSp.era} ERA)` : ""}{" "}
      <span className="text-brand-muted">vs</span> {oppSp?.name || "TBD"}{" "}
      {oppSp?.era ? `(${oppSp.era} ERA)` : ""}
    </p>
  );
}

function NextGameBlock({ nextGame }) {
  if (!nextGame) return null;
  const hasOpponent = nextGame.opponent_name || nextGame.opponent_abbr;
  if (!hasOpponent) return null;
  return (
    <div className="mb-5 flex items-start gap-3 rounded-xl bg-white border border-dashed border-brand-black/25 p-4">
      <div className="min-w-0">
        <p className="text-[10px] font-bold tracking-[0.18em] text-brand-muted-strong uppercase mb-1">
          Next Up
        </p>
        <p className="text-sm font-semibold text-brand-black">
          {nextGame.home ? "vs" : "@"}{" "}
          {nextGame.opponent_name || nextGame.opponent_abbr}
        </p>
        {nextGame.game_time && (
          <p className="mt-0.5 text-xs text-brand-muted-strong">
            {formatGameTime(nextGame.game_time)}
          </p>
        )}
        <ProbablePitchersLine ourSp={nextGame.our_sp} oppSp={nextGame.opp_sp} />
      </div>
    </div>
  );
}

function TeamNewsBlock({ news }) {
  if (!news || news.length === 0) return null;
  return (
    <div>
      <p className="text-[10px] font-bold tracking-[0.18em] text-brand-muted-strong uppercase mb-2">
        Team news
      </p>
      <ul className="space-y-1.5">
        {news.slice(0, 4).map((item, i) => {
          const text =
            typeof item === "string"
              ? item
              : item.text || item.description || item.note || "";
          return (
            <li
              key={stableNewsKey(item, i)}
              className="flex gap-2.5 text-sm text-brand-black leading-snug"
            >
              <span
                aria-hidden="true"
                className="mt-1.5 inline-block w-1.5 h-1.5 bg-brand-red shrink-0"
              />
              <span>{text}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function TeamDeepDiveCard({ deepDive }) {
  if (!deepDive) return null;
  const { team, last_game, standing, news, next_game, edge_analysis } = deepDive;
  return (
    <article
      data-testid={`briefing-deep-dive-${team.abbr}`}
      className="relative rounded-2xl bg-white border border-brand-black/10 shadow-card overflow-hidden"
    >
      <PrimaryColorBar color={team.primary_color} />
      <div className="p-6 md:p-7 pt-7 md:pt-8">
        <DeepDiveHeader team={team} standing={standing} />
        <LastGameBlock lastGame={last_game} teamAbbr={team.abbr} />
        <NextGameBlock nextGame={next_game} />
        <EdgeAnalysisBlock text={edge_analysis} />
        <TeamNewsBlock news={news} />
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Movers
// ---------------------------------------------------------------------------

function MoverCard({ mover, idx }) {
  const up = mover.css_class && mover.css_class.startsWith("up");
  const boxClass = up
    ? "bg-white border-brand-black/25"
    : "bg-brand-red-soft border-brand-red/30";
  const labelClass = up ? "text-brand-black" : "text-brand-red";
  return (
    <div
      data-testid={`briefing-mover-${idx}`}
      className={`border p-4 ${boxClass}`}
    >
      <div
        className={`inline-flex items-center gap-1 font-mono text-xs font-bold tracking-wider uppercase ${labelClass}`}
      >
        <span aria-hidden="true">{up ? "▲" : "▼"}</span>
        {mover.display}
      </div>
      <p className="mt-2 font-serif text-lg font-black text-brand-black leading-tight">
        {mover.team?.name || mover.team?.abbr || "—"}
      </p>
      {mover.new_rank && (
        <p className="mt-1 font-mono text-xs font-semibold text-brand-muted-strong">
          Now ranked #{mover.new_rank}
        </p>
      )}
    </div>
  );
}

function MoversGrid({ movers }) {
  if (!movers || movers.length === 0) return null;
  return (
    <section
      data-testid="briefing-movers"
      className="rounded-2xl bg-white border border-brand-black/10 shadow-card p-6 md:p-8"
    >
      <SectionHeader
        eyebrow="Power Rankings"
        title="Biggest Movers"
        subtitle="Week-over-week shifts in our power rankings."
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {movers.map((m, idx) => (
          <MoverCard key={stableMoverKey(m, idx)} mover={m} idx={idx} />
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Standings
// ---------------------------------------------------------------------------

function StandingsRow({ team, idx }) {
  const rowClass = standingsRowClass(idx);
  return (
    <tr className={`${rowClass} border-t border-brand-black/5`}>
      <td className="px-3 py-2 text-brand-black font-semibold">
        {idx === 0 && (
          <span
            className="inline-block mr-1.5 text-brand-red"
            title="Division leader"
          >
            ▸
          </span>
        )}
        {team.team_name}
      </td>
      <td className="px-2 py-2 text-right tabular-nums text-brand-black">
        {team.w}-{team.l}
      </td>
      <td className="px-2 py-2 text-right tabular-nums text-brand-muted-strong">
        {team.pct}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-brand-muted-strong">
        {team.gb}
      </td>
    </tr>
  );
}

function DivisionTable({ divisionName, teams }) {
  return (
    <div
      data-testid={`briefing-division-${divisionName.replace(/\s+/g, "-")}`}
    >
      <p className="text-[11px] font-bold tracking-[0.18em] text-brand-muted-strong uppercase mb-3">
        {divisionName}
      </p>
      <div className="rounded-xl border border-brand-black/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-brand-paper">
            <tr>
              <th className="text-left px-3 py-2 text-[11px] font-bold tracking-wider uppercase text-brand-muted-strong">
                Team
              </th>
              <th className="text-right px-2 py-2 text-[11px] font-bold tracking-wider uppercase text-brand-muted-strong">
                W-L
              </th>
              <th className="text-right px-2 py-2 text-[11px] font-bold tracking-wider uppercase text-brand-muted-strong">
                Pct
              </th>
              <th className="text-right px-3 py-2 text-[11px] font-bold tracking-wider uppercase text-brand-muted-strong">
                GB
              </th>
            </tr>
          </thead>
          <tbody>
            {teams.map((team, idx) => (
              <StandingsRow
                key={team.mlb_team_id || team.team_name || idx}
                team={team}
                idx={idx}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StandingsTable({ standings }) {
  if (!standings || Object.keys(standings).length === 0) return null;
  const orderedDivisions = DIVISION_ORDER.filter(
    (d) => standings[d] && standings[d].length > 0,
  );
  return (
    <section
      data-testid="briefing-standings"
      className="rounded-2xl bg-white border border-brand-black/10 shadow-card p-6 md:p-8"
    >
      <SectionHeader
        eyebrow="The Order"
        title="League Standings"
        subtitle="Every division, top to bottom. Through last night's slate."
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {orderedDivisions.map((divName) => (
          <DivisionTable
            key={divName}
            divisionName={divName}
            teams={standings[divName]}
          />
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Trend Watch
// ---------------------------------------------------------------------------

function TrendItem({ item, idx }) {
  const teamLabel =
    item.team?.name ||
    item.team?.abbr ||
    (typeof item.team === "string" ? item.team : "");
  // Player rows have a separate name; team rows repeat the team in name.
  const isTeamRow =
    !item.name || (teamLabel && item.name.endsWith(teamLabel));
  const primary = item.name || teamLabel || "—";
  return (
    <li className="flex items-center gap-3 rounded-xl border border-brand-black/5 bg-brand-paper px-4 py-3">
      <span className="shrink-0 w-6 h-6 inline-flex items-center justify-center rounded-md bg-brand-black text-white font-bold text-[11px]">
        {idx + 1}
      </span>
      <div className="flex-1 min-w-0 flex items-baseline justify-between gap-3">
        <p className="text-sm md:text-base font-semibold text-brand-black truncate">
          {primary}
          {!isTeamRow && teamLabel && (
            <span className="text-brand-muted-strong font-normal">
              {" "}
              · {teamLabel}
            </span>
          )}
        </p>
        <p className="text-sm font-extrabold text-brand-red tabular-nums whitespace-nowrap">
          {item.stat_value}
        </p>
      </div>
    </li>
  );
}

function TrendWatchCard({ trend }) {
  if (!trend || !trend.items || trend.items.length === 0) return null;
  const items = trend.items.slice(0, 5);
  return (
    <section
      data-testid="briefing-trend-watch"
      className="rounded-2xl bg-white border border-brand-black/10 shadow-card p-6 md:p-8"
    >
      <SectionHeader
        eyebrow="Trend watch"
        title={trend.category || "Today's Trend"}
        subtitle={trend.day ? `${trend.day} edition` : null}
      />
      <ol className="space-y-2.5">
        {items.map((item, idx) => (
          <TrendItem key={stableTrendKey(item, idx)} item={item} idx={idx} />
        ))}
      </ol>
      {items[0]?.stat_label && (
        <p className="mt-3 text-xs text-brand-muted-strong">
          Measured by {items[0].stat_label}.
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Transactions
// ---------------------------------------------------------------------------

function TransactionRow({ item }) {
  const teamAbbr = item.team || item.team_abbr || "";
  const text = item.description || item.note || item.text || "";
  return (
    <li className="flex items-start gap-3 rounded-xl border border-brand-black/5 bg-brand-paper px-4 py-3">
      {teamAbbr && (
        <span className="shrink-0 inline-flex items-center justify-center min-w-[2.25rem] h-6 px-2 rounded-md bg-brand-black text-white font-bold text-[10px] tracking-wider">
          {teamAbbr}
        </span>
      )}
      <p className="text-sm text-brand-black leading-snug">{text}</p>
    </li>
  );
}

function TransactionsCard({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <section
      data-testid="briefing-transactions"
      className="rounded-2xl bg-white border border-brand-black/10 shadow-card p-6 md:p-8"
    >
      <SectionHeader
        eyebrow="Around the league"
        title="Transactions & Injury List"
        subtitle="Roster moves and IL changes from the last 48 hours."
      />
      <ul className="space-y-2.5">
        {items.slice(0, 12).map((item, idx) => (
          <TransactionRow key={stableTxKey(item, idx)} item={item} />
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Fun fact
// ---------------------------------------------------------------------------

function FunFactCard({ fact }) {
  if (!fact || !fact.text) return null;
  return (
    <section
      data-testid="briefing-fun-fact"
      className="rounded-2xl bg-brand-black text-white p-6 md:p-8 shadow-card relative overflow-hidden"
    >
      <span
        aria-hidden="true"
        className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-brand-red/20 blur-3xl"
      />
      <div className="relative">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-brand-red/50 font-mono text-[10px] font-bold tracking-[0.22em] uppercase text-brand-red">
          Fun fact
        </div>
        <p className="mt-4 font-serif text-lg md:text-xl font-bold leading-snug">
          {fact.text}
        </p>
        {fact.attribution && (
          <p className="mt-3 text-xs text-white/60 tracking-wide uppercase">
            {fact.attribution}
          </p>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Header / status / sections
// ---------------------------------------------------------------------------

function BriefingHeader({ data }) {
  const teamLabel = data.team_count === 1 ? "team" : "teams";
  return (
    <header
      data-testid="briefing-header"
      className="rounded-2xl bg-white border border-brand-black/10 shadow-card p-6 md:p-10 mb-6 md:mb-8 relative overflow-hidden"
    >
      <span
        aria-hidden="true"
        className="absolute top-0 left-0 right-0 h-1.5 bg-brand-red"
      />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div className="min-w-0">
          <p
            data-testid="briefing-eyebrow"
            className="text-[11px] font-bold tracking-[0.22em] text-brand-red uppercase"
          >
            Today's Briefing
          </p>
          <h1
            data-testid="briefing-title"
            className="mt-2 font-serif font-black text-[1.6rem] md:text-[2.55rem] text-brand-black leading-[1.05]"
          >
            The Box Score Club
          </h1>
          <p
            data-testid="briefing-date"
            className="mt-3 font-mono text-xs md:text-sm font-semibold tracking-[0.18em] text-brand-muted-strong uppercase"
          >
            {data.date_label || data.date}
          </p>
        </div>
        <div className="md:text-right shrink-0 flex md:flex-col gap-4 md:gap-2 items-end">
          {data.team_count > 0 && (
            <div className="text-xs">
              <p className="font-bold tracking-[0.18em] text-brand-muted-strong uppercase">
                Tracking
              </p>
              <p className="mt-1 text-2xl font-extrabold text-brand-black">
                {data.team_count}{" "}
                <span className="text-base font-semibold text-brand-muted-strong">
                  {teamLabel}
                </span>
              </p>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function DeepDiveSection({ deepDives }) {
  if (!deepDives || deepDives.length === 0) return null;
  const gridClass =
    deepDives.length === 1 ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-2";
  return (
    <div data-testid="briefing-deep-dives">
      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[0.22em] text-brand-red uppercase">
          Your teams
        </p>
        <h2 className="mt-2 font-serif text-2xl md:text-3xl font-black text-brand-black tracking-tight">
          Deep Dive
        </h2>
        <p className="mt-1 text-sm text-brand-muted-strong">
          Last night's line, the edge, and what's next.
        </p>
      </div>
      <div className={`grid gap-4 md:gap-6 ${gridClass}`}>
        {deepDives.map((dd) => (
          <TeamDeepDiveCard
            key={dd.team?.abbr || dd.team?.name || `dd-${deepDives.indexOf(dd)}`}
            deepDive={dd}
          />
        ))}
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div
      data-testid="briefing-loading"
      className="flex flex-col items-center justify-center py-32 gap-4"
    >
      <div className="w-12 h-12 rounded-full border-2 border-brand-black/10 border-t-brand-red animate-spin" />
      <p className="text-sm text-brand-muted-strong">
        Loading today's briefing…
      </p>
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div
      data-testid="briefing-error"
      className="mt-10 rounded-2xl border border-brand-black/10 bg-white p-10 text-center max-w-xl mx-auto shadow-card"
    >
      <p className="text-base font-semibold text-brand-black">{message}</p>
      <p className="mt-2 text-sm text-brand-muted-strong">
        If you keep seeing this, reply to your last recap email and we'll fix it
        fast.
      </p>
    </div>
  );
}

function BriefingBody({ data }) {
  const generatedDate = data.generated_at?.split("T")[0] || "";
  return (
    <div className="space-y-6 md:space-y-8">
      <FadeUp delay={80}>
        <HeadlinesCard headlines={data.headlines} />
      </FadeUp>
      <FadeUp delay={160}>
        <DeepDiveSection deepDives={data.deep_dives} />
      </FadeUp>
      <FadeUp delay={220}>
        <MoversGrid movers={data.movers} />
      </FadeUp>
      <FadeUp delay={280}>
        <StandingsTable standings={data.standings} />
      </FadeUp>
      <FadeUp delay={340}>
        <TrendWatchCard trend={data.trend_watch} />
      </FadeUp>
      <FadeUp delay={400}>
        <TransactionsCard items={data.tx_and_il} />
      </FadeUp>
      <FadeUp delay={460}>
        <FunFactCard fact={data.fun_fact} />
      </FadeUp>
      <div className="text-center pt-4">
        <p className="text-xs text-brand-muted">
          Briefing generated {generatedDate} · The Box Score Club
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function BriefingPage() {
  const { token } = useParams();
  const { data, error, loading } = useBriefingData(token);

  let body;
  if (loading) {
    body = <LoadingState />;
  } else if (error) {
    body = <ErrorState message={error} />;
  } else if (data) {
    body = (
      <>
        <FadeUp>
          <BriefingHeader data={data} />
        </FadeUp>
        <BriefingBody data={data} />
      </>
    );
  } else {
    body = null;
  }

  return (
    <div className="min-h-screen bg-brand-paper text-brand-black flex flex-col">
      <BaseballCursor size={0.75} />
      <Nav />
      <main className="flex-1 pt-24 pb-16 px-4 md:px-6">
        <div className="max-w-5xl mx-auto">{body}</div>
      </main>
      <SiteFooter />
    </div>
  );
}
